import Card from '../../components/ui/Card';
import StatusTag from './StatusTag';
import type { TagTone } from './StatusTag';
import type { MirroredDto } from './types';

// BP 27 §First-hand vs mirrored — the D2L section. Every row here is a
// LABELLED COPY, never something ACE observed: the badge is not decoration,
// it is the thing that keeps a three-week-old grade copy from ever reading
// as first-hand evidence. docs/IMPLEMENTATION-PLAN-22-27.md §8.3 requires
// every mirrored row to carry this badge — see index.test.tsx.
function fmtDate(d: string | null | undefined): string {
  return d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
}

function badgeText(item: MirroredDto['items'][number]): string {
  return item.source === 'd2l_import'
    ? `D2L · imported ${fmtDate(item.observed_at)}`
    : `D2L · instructor-observed ${fmtDate(item.observed_at)}`;
}

const STATUS_TONE: Record<string, TagTone> = {
  passed: 'success',
  graded: 'success',
  needs_retry: 'danger',
  awaiting_results: 'accent',
  submitted: 'accent',
  in_progress: 'accent',
  not_started: 'neutral',
};

/** The D2L assessment-status mirror (BP 25) — always a separate section,
 * never blended into first_hand counts or rows. */
export default function MirrorPanel({ mirrored }: { mirrored: MirroredDto }) {
  return (
    <Card className="overflow-hidden print:break-inside-avoid">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-ws-border bg-ws-surface-2 px-4 py-2.5">
        <div>
          <h3 className="text-sm font-semibold text-ws-ink">D2L mirror</h3>
          <p className="text-[11px] text-ws-ink-3">
            Assessed work stays in D2L (DC Connect). This is a labelled copy of status only.
          </p>
        </div>
        <span className="text-[11px] text-ws-ink-3">
          {mirrored.latest_import
            ? `Last synced ${fmtDate(mirrored.latest_import.exported_at)}`
            : 'Never imported'}
        </span>
      </div>

      {mirrored.items.length === 0 ? (
        <p className="px-4 py-6 text-center text-sm text-ws-ink-3">No D2L status has been imported yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-ws-border text-left text-xs text-ws-ink-3">
                <th className="px-4 py-2 font-medium">Item</th>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-4 py-2 font-medium">Score</th>
                <th className="px-4 py-2 font-medium">Source</th>
              </tr>
            </thead>
            <tbody>
              {mirrored.items.map((item) => (
                <tr key={item.key} className="border-b border-ws-border last:border-0">
                  <td className="px-4 py-2 text-ws-ink">{item.title}</td>
                  <td className="px-4 py-2">
                    <StatusTag label={item.status.replace(/_/g, ' ')} tone={STATUS_TONE[item.status] || 'neutral'} size="sm" />
                  </td>
                  <td className="px-4 py-2 text-ws-ink-2">
                    {item.score != null ? `${item.score}${item.max_score != null ? ` / ${item.max_score}` : ''}` : '—'}
                  </td>
                  <td className="px-4 py-2">
                    <span data-testid={`mirror-badge-${item.key}`} className="text-[11px] font-medium text-ws-ink-3">
                      {badgeText(item)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
